import React, { useEffect, useState } from "react";
import { api, inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table";
import { Plus, PencilSimple, Wrench } from "@phosphor-icons/react";
import { toast } from "sonner";
import ImageUpload from "@/components/ImageUpload";

const STATUSES = ["received", "in_progress", "ready", "delivered"];
const STATUS_COLORS = {
  received: "text-muted-foreground",
  in_progress: "text-orange-400",
  ready: "text-gold",
  delivered: "text-green-400",
};

export default function Repairs() {
  const [repairs, setRepairs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank());

  function blank() {
    return {
      customer_id: "",
      item_type: "",
      description: "",
      charges: 0,
      before_photo_url: "",
      after_photo_url: "",
      delivery_date: "",
      status: "received",
    };
  }

  const load = async () => {
    const [r, c] = await Promise.all([
      api.get("/repairs"),
      api.get("/customers"),
    ]);
    setRepairs(r.data);
    setCustomers(c.data);
  };
  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm(blank());
    setOpen(true);
  };
  const openEdit = (r) => {
    setEditing(r);
    setForm({ ...blank(), ...r });
    setOpen(true);
  };

  const save = async () => {
    if (!form.customer_id || !form.item_type) {
      toast.error("Customer and item type required");
      return;
    }
    if (saving) return;
    setSaving(true);
    try {
      const payload = { ...form, charges: Number(form.charges) || 0 };
      [
        "description",
        "before_photo_url",
        "after_photo_url",
        "delivery_date",
      ].forEach((k) => {
        if (!payload[k]) payload[k] = null;
      });
      if (editing) await api.put(`/repairs/${editing.id}`, payload);
      else await api.post("/repairs", payload);
      toast.success(editing ? "Updated" : "Repair logged");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="repairs-page">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            Aftersales
          </div>
          <h1 className="font-display text-3xl md:text-4xl tracking-tight mt-1">
            Repairs
          </h1>
        </div>
        <Button
          data-testid="new-repair-btn"
          onClick={openNew}
          className="bg-primary text-primary-foreground hover:bg-primary/90"
        >
          <Plus size={16} className="mr-1.5" /> New repair
        </Button>
      </header>

      <div className="rounded-lg border border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/40 hover:bg-secondary/40">
              <TableHead>Job</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Item</TableHead>
              <TableHead className="text-right">Charges</TableHead>
              <TableHead>Delivery</TableHead>
              <TableHead>Status</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {repairs.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="text-center py-10 text-muted-foreground"
                >
                  No repair jobs yet.
                </TableCell>
              </TableRow>
            ) : (
              repairs.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="font-mono text-xs">
                    {r.repair_no}
                  </TableCell>
                  <TableCell>{r.customer_name || "—"}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Wrench size={14} className="text-gold" />
                      {r.item_type}
                    </div>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {inr(r.charges)}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {r.delivery_date || "—"}
                  </TableCell>
                  <TableCell>
                    <span
                      className={`text-xs uppercase tracking-wider ${STATUS_COLORS[r.status]}`}
                    >
                      {r.status.replace("_", " ")}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <button
                      onClick={() => openEdit(r)}
                      className="p-1.5 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground"
                    >
                      <PencilSimple size={14} />
                    </button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              {editing ? "Edit" : "New"} Repair
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
                Customer *
              </Label>
              <Select
                value={form.customer_id}
                onValueChange={(v) => setForm({ ...form, customer_id: v })}
              >
                <SelectTrigger
                  data-testid="repair-customer"
                  className="mt-1.5 bg-background border-border"
                >
                  <SelectValue placeholder="Choose" />
                </SelectTrigger>
                <SelectContent>
                  {customers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name} · {c.mobile}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
                Status
              </Label>
              <Select
                value={form.status}
                onValueChange={(v) => setForm({ ...form, status: v })}
              >
                <SelectTrigger className="mt-1.5 bg-background border-border">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => (
                    <SelectItem key={s} value={s} className="capitalize">
                      {s.replace("_", " ")}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
                Item type *
              </Label>
              <Input
                data-testid="repair-item"
                className="mt-1.5 bg-background border-border"
                value={form.item_type}
                onChange={(e) =>
                  setForm({ ...form, item_type: e.target.value })
                }
                placeholder="e.g. 22K gold chain - lock broken"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
                Issue description
              </Label>
              <Input
                className="mt-1.5 bg-background border-border"
                value={form.description ?? ""}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
              />
            </div>
            <Field
              label="Charges (₹)"
              type="number"
              value={form.charges}
              onChange={(v) => setForm({ ...form, charges: v })}
            />
            <Field
              label="Delivery date"
              type="date"
              value={form.delivery_date}
              onChange={(v) => setForm({ ...form, delivery_date: v })}
            />
            <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
              <ImageUpload
                testid="repair-before-image"
                label="Before photo"
                value={form.before_photo_url}
                onChange={(v) => setForm({ ...form, before_photo_url: v })}
              />
              <ImageUpload
                testid="repair-after-image"
                label="After photo"
                value={form.after_photo_url}
                onChange={(v) => setForm({ ...form, after_photo_url: v })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              data-testid="save-repair-btn"
              onClick={save}
              loading={saving}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, value, onChange, type = "text" }) => (
  <div>
    <Label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
      {label}
    </Label>
    <Input
      type={type}
      className="mt-1.5 bg-background border-border"
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
    />
  </div>
);
